"use client";
import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';

const ModalModal = ({ isOpen, onClose, modal, categories, subCategories, models = [], onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'Active',
    image: '',
    categoryId: '',
    subCategoryId: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const modalRef = useRef(null);
  const fileInputRef = useRef(null);

  const filteredSubCategories = subCategories.filter(
    (sub) => String(sub.category_id) === String(formData.categoryId)
  );

  useEffect(() => {
    if (modal) {
      setFormData({
        name: modal.model_name || '',
        description: modal.description || '',
        status: modal.status || 'Active',
        image: modal.image || '',
        categoryId: modal.category_id ? String(modal.category_id) : '',
        subCategoryId: modal.sub_category_id ? String(modal.sub_category_id) : '',
      });
      setImagePreview(modal.image || '');
    } else {
      setFormData({ name: '', description: '', status: 'Active', image: '', categoryId: '', subCategoryId: '' });
      setImagePreview('');
    }
    setErrors({});
  }, [modal, isOpen]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => modalRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const updatedValue = name === 'name' ? value.charAt(0).toUpperCase() + value.slice(1) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : updatedValue,
      ...(name === 'categoryId' && { subCategoryId: '' }),
    }));

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleImageUpload = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setFormData((prev) => ({ ...prev, image: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImagePreview('');
    setFormData((prev) => ({ ...prev, image: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

 const validateForm = () => {
  const newErrors = {};

  const trimmedName = formData.name.trim();

  if (!trimmedName) {
    newErrors.name = 'Model name is required';
  } else if (trimmedName.length < 2) {
    newErrors.name = 'Model name must be at least 2 characters';
  } else {
    // Duplicate check: same name within the same sub-category
    const isDuplicate = models.some(m => {
      if (modal && String(m.id) === String(modal.id)) return false; // skip self on edit
      const sameSubCategory = String(m.sub_category_id) === String(formData.subCategoryId);
      const sameName = (m.model_name || '').trim().toLowerCase() === trimmedName.toLowerCase();
      return sameSubCategory && sameName;
    });

    if (isDuplicate) {
      newErrors.name = 'A model with this name already exists in the selected sub-category';
    }
  }

  if (!formData.description.trim()) newErrors.description = 'Description is required';
  else if (formData.description.trim().length < 10) newErrors.description = 'Description must be at least 10 characters';
  if (!formData.categoryId) newErrors.categoryId = 'Please select a category';
  if (!formData.subCategoryId) newErrors.subCategoryId = 'Please select a sub-category';

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const selectedCategory = categories.find((c) => String(c.id) === String(formData.categoryId));
      const selectedSubCategory = subCategories.find((s) => String(s.id) === String(formData.subCategoryId));
      await onSave({
        ...formData,
        id: modal?.id || Date.now().toString(),
        categoryName: selectedCategory?.name || '',
        subCategoryName: selectedSubCategory?.subcat_name || '',
        createdAt: modal?.createdAt || new Date().toISOString().split('T')[0],
        updatedAt: new Date().toISOString().split('T')[0],
      });
      onClose();
    } catch {
      setErrors({ general: 'Failed to save model. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} aria-hidden="true" />

      <div
        ref={modalRef}
        className={`fixed top-0 right-0 h-full w-full sm:max-w-xl bg-white shadow-2xl z-50 flex flex-col overflow-hidden transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-teal-700 px-6 py-5 flex items-center justify-between sticky top-0 z-10">
          <h2 id="modal-title" className="text-lg font-semibold text-white tracking-tight">
            {modal ? 'Edit Model' : 'Add New Model'}
          </h2>
          <button onClick={onClose} disabled={isLoading}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close modal">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-5"
        >
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
              {errors.general}
            </div>
          )}

          {/* Category */}
          <Field label="Category" error={errors.categoryId} hint="Choose the main category">
            <select
              id="categoryId"
              name="categoryId"
              value={formData.categoryId}
              onChange={handleInputChange}
              disabled={isLoading}
              className={selectClass(errors.categoryId)}
            >
              <option value="">Select a category...</option>
              {categories
                ?.filter((cat) => cat.status === 'Active' || cat.status === 1 || cat.status === '1')
                .map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
            </select>
          </Field>

          {/* Sub-Category */}
          <Field label="Sub-Category" error={errors.subCategoryId} hint={!formData.categoryId ? 'Select a category first' : 'Choose the sub-category'}>
            <select
              id="subCategoryId"
              name="subCategoryId"
              value={formData.subCategoryId}
              onChange={handleInputChange}
              disabled={!formData.categoryId || isLoading}
              className={`${selectClass(errors.subCategoryId)} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <option value="">{!formData.categoryId ? 'Select category first...' : 'Select sub-category...'}</option>
              {filteredSubCategories.map((sub) => (
                <option key={sub.id} value={sub.id}>{sub.subcat_name}</option>
              ))}
            </select>
          </Field>

          {/* Model Name */}
          <Field label="Model Name *" error={errors.name} hint="Make it specific and descriptive" maxLength={50} currentLength={formData.name.length}>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter model name..."
              maxLength={50}
              disabled={isLoading}
              className={inputClass(errors.name)}
            />
          </Field>

          {/* Description */}
          <Field label="Description *" error={errors.description} hint="Describe what products belong in this model" maxLength={200} currentLength={formData.description.length}>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter model description..."
              rows={4}
              maxLength={200}
              disabled={isLoading}
              className={`${inputClass(errors.description)} resize-none`}
            />
          </Field>

          {/* Status */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              {['Active', 'Inactive'].map((val) => (
                <label key={val} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value={val}
                    checked={formData.status === val}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    className="sr-only"
                  />
                  <div className={`relative w-5 h-5 rounded-full border-2 transition-all ${
                    formData.status === val
                      ? val === 'Active' ? 'border-green-500 bg-green-500' : 'border-gray-500 bg-gray-500'
                      : 'border-gray-300 bg-white'
                  }`}>
                    {formData.status === val && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full" />
                      </div>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${
                    formData.status === val
                      ? val === 'Active' ? 'text-green-700' : 'text-gray-700'
                      : 'text-gray-600'
                  }`}>{val}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium hidden sm:inline ${
                    formData.status === val && val === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {val === 'Active' ? 'Visible to customers' : 'Hidden from customers'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
                      Model Image <span className="font-normal text-gray-500">(Optional)</span>
            </label>
            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden border border-gray-200">
                <img src={imagePreview} alt="Preview" className="w-full h-36 sm:h-44 object-cover" />
                <button
                  type="button"
                  onClick={removeImage}
                  disabled={isLoading}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
                  aria-label="Remove image"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                className={`border-2 border-dashed rounded-xl p-6 sm:p-8 text-center transition-colors ${
                  isDragging ? 'border-teal-400 bg-teal-50' : 'border-gray-300 hover:border-teal-300 bg-white/60'
                }`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                onDrop={(e) => { e.preventDefault(); setIsDragging(false); handleImageUpload(e.dataTransfer.files[0]); }}
              >
                <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-3">Drag and drop an image here, or</p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 text-sm"
                >
                  <Upload className="w-4 h-4" /> Choose File
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                  className="hidden"
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-400 mt-3">PNG, JPG up to 5MB</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <button type="button" onClick={onClose} disabled={isLoading}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={isLoading}
              className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors shadow-sm flex items-center justify-center gap-2">
              {isLoading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
              ) : (
                modal ? 'Update Model' : 'Create Model'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const inputClass = (error) =>
  `w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all text-gray-800 placeholder-gray-400 text-sm ${
    error ? 'border-red-300' : 'border-gray-200'
  }`;

const selectClass = (error) =>
  `w-full px-4 py-3 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-all text-gray-800 cursor-pointer text-sm ${
    error ? 'border-red-300' : 'border-gray-200'
  }`;

const Field = ({ label, error, hint, maxLength, currentLength, children }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    {children}
    <div className="flex justify-between">
      <p className={`text-xs ${error ? 'text-red-500' : 'text-gray-500'}`}>{error || hint}</p>
      {maxLength != null && (
        <span className="text-xs text-gray-400 shrink-0 ml-2">{currentLength}/{maxLength}</span>
      )}
    </div>
  </div>
);

export default ModalModal;